import { useCallback, useEffect, useState } from "react";
import { Drawer, drawerClasses, IconButton, Stack, styled, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";

import { Chip } from "~/common/m3/Chip/Chip";
import { STICKY_TO_BOTTOM } from "~/common/components/icon/Elevation";
import { verticalStacker } from "~/styles/styledMixins";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Button } from "~/common/components/Button/Button";
import ReduxSortingPicker from "~/Search/components/common/ReduxSortingPicker";
import { ReduxFilterSection } from "~/Search/components/Filters/ReduxFilterSection";
import { useSelectAppliedFilters } from "~/Search/components/Filters/components/AppliedFilters/useSelectAppliedFilters";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { applyAllFiltersAction, clearAll } from "~/Search/redux/actions";
import { useSearchPageType } from "~/Search/useSearchPageType";
import { useForm } from "react-hook-form";
import { flattenTechFilterFormData, unflattenTechFilterFormData } from "~/Search/utils/flattenTechFilterFormData";
import { DialogActions, DialogContent } from "~/common/m3/Dialog/Dialog";
import { selectAppliedTechFilterValues } from "~/Search/redux/searchSelectors";

export const FilterSortChip = () => {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const dispatch = useDispatch();
  const isMinTablet = useIsMinTabletSize();
  const appliedTechFilterValues = useSelector(selectAppliedTechFilterValues);

  const methods = useForm({
    values: unflattenTechFilterFormData(appliedTechFilterValues),
    mode: "onChange",
  });

  useEffect(() => {
    if (isDrawerOpen) {
      methods.reset();
    }
  }, [appliedTechFilterValues, methods, isDrawerOpen]);

  const pageType = useSearchPageType();

  const handleClose = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const handleClearAll = useCallback(() => {
    dispatch(clearAll(pageType));
  }, [dispatch, pageType]);

  const onSubmit = (data: Record<string, { min?: number | undefined; max?: number | undefined }>) => {
    const flattenedData = flattenTechFilterFormData(data);
    dispatch(applyAllFiltersAction(flattenedData));
    handleClose();
  };

  const applyCb = methods.handleSubmit(onSubmit);

  const breadcrumbLength = useSelectAppliedFilters().length;
  let label;
  if (breadcrumbLength === 0) {
    label = t("page.search.chip.filter.empty");
  } else {
    label = t("page.search.chip.filter.selected", { count: breadcrumbLength });
  }
  return (
    <>
      <Chip
        variant={"filter"}
        onClick={() => setIsDrawerOpen(true)}
        icon={<FilterAltOutlinedIcon />}
        label={label}
        selected={breadcrumbLength > 0}
      />
      <StyledDrawer anchor={isMinTablet ? "left" : "bottom"} open={isDrawerOpen} onClose={handleClose}>
        <DrawerContent sx={{ ...verticalStacker("24px") }}>
          <DrawerHeader>
            <Typography
              variant="titleLarge"
              sx={{
                fontWeight: "bold",
              }}
            >
              Filter & sort
            </Typography>
            <IconButton onClick={handleClose} color={"secondary"} variant={"contained"}>
              <Close />
            </IconButton>
          </DrawerHeader>
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Typography
              variant={"titleMedium"}
              sx={{
                fontWeight: "bold",
              }}
            >
              {t("searchPage.sort.title")}
            </Typography>
            <ReduxSortingPicker />
          </Stack>

          <form onSubmit={applyCb}>
            <ReduxFilterSection
              titleFontVariant={"titleMedium"}
              hideTechFilterButtons
              methods={methods}
              onSubmit={onSubmit}
            />
          </form>
        </DrawerContent>

        <DrawerActions>
          <Button variant="text" fullWidth onClick={handleClearAll}>
            {t("searchPage.refinement.button.clearAll")}
          </Button>
          <Button fullWidth onClick={applyCb} disabled={!methods.formState.isDirty}>
            {t("searchPage.refinement.button.apply")}
          </Button>
        </DrawerActions>
      </StyledDrawer>
    </>
  );
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`.${drawerClasses.paper}`]: {
    [theme.breakpoints.down("tablet")]: {
      height: "70vh",
      maxHeight: "700px",
    },
    [theme.breakpoints.between("tablet", "desktop-s")]: {
      width: "45vw",
      maxWidth: "550px",
    },
  },
}));

const DrawerHeader = styled("div")({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const DrawerContent = styled(DialogContent)({
  padding: "16px",
});

const DrawerActions = styled(DialogActions)({
  padding: "8px 16px",
  boxShadow: STICKY_TO_BOTTOM,
  zIndex: 1,
});
